// Filter chips and segmented tabs (UX02). Owner: WP04.
import { useRef, type KeyboardEvent, type ReactNode } from 'react';

export type ChipProps = { selected?: boolean; icon?: ReactNode; onClick?: () => void; children: ReactNode; disabled?: boolean; className?: string };

/** Toggleable filter chip. Selected state is lime + aria-pressed (never color only: a check-weight change too). */
export function Chip({ selected = false, icon, onClick, children, disabled, className }: ChipProps) {
  return (
    <button type="button" className={['ui-chip', selected ? 'is-selected' : '', className ?? ''].filter(Boolean).join(' ')} aria-pressed={selected} onClick={onClick} disabled={disabled}>
      {icon ? <span className="ui-chip__icon" aria-hidden>{icon}</span> : null}
      {children}
    </button>
  );
}

export type TabItem<T extends string> = { value: T; label: string; icon?: ReactNode };
export type SegmentedTabsProps<T extends string> = {
  /** Accessible name of the tab list. */
  label: string;
  items: TabItem<NoInfer<T>>[];
  value: T;
  onChange(value: T): void;
  /** id of the panel these tabs control (sets aria-controls). */
  panelId?: string;
  idPrefix?: string;
};

/** ARIA tabs with roving focus: Left/Right/Home/End move between tabs, selection follows focus. */
export function SegmentedTabs<T extends string>({ label, items, value, onChange, panelId, idPrefix = 'tab' }: SegmentedTabsProps<T>) {
  const ref = useRef<HTMLDivElement>(null);
  const onKeyDown = (e: KeyboardEvent) => {
    const i = items.findIndex((t) => t.value === value);
    const next = e.key === 'ArrowRight' ? i + 1 : e.key === 'ArrowLeft' ? i - 1 : e.key === 'Home' ? 0 : e.key === 'End' ? items.length - 1 : null;
    if (next === null) return;
    e.preventDefault();
    const t = items[(next + items.length) % items.length]!;
    onChange(t.value);
    ref.current?.querySelector<HTMLButtonElement>(`[data-value="${t.value}"]`)?.focus();
  };
  return (
    <div className="ui-tabs" role="tablist" aria-label={label} ref={ref} onKeyDown={onKeyDown}>
      {items.map((t) => {
        const selected = t.value === value;
        return (
          <button
            key={t.value}
            type="button"
            role="tab"
            id={`${idPrefix}-${t.value}`}
            data-value={t.value}
            aria-selected={selected}
            aria-controls={panelId}
            tabIndex={selected ? 0 : -1}
            className={selected ? 'ui-tab is-selected' : 'ui-tab'}
            onClick={() => onChange(t.value)}
          >
            {t.icon ? <span aria-hidden>{t.icon}</span> : null}
            {t.label}
          </button>
        );
      })}
    </div>
  );
}
